import React, { useState, useRef, useEffect, useMemo, useContext, useCallback } from 'react';
import { Button, Table } from 'react-bootstrap';
import AnalogClock from './AnalogClock';
import { ThemeContext } from '../contexts/ThemeContext';

const DAY_NAMES = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DetachableClock = ({ onClose }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Position on the right side by default, with localStorage support
  const [position, setPosition] = useState(() => {
    // Try to load saved position from localStorage
    const savedPosition = localStorage.getItem('clockPosition');
    if (savedPosition) {
      try {
        return JSON.parse(savedPosition);
      } catch (e) {
        console.error('Error parsing saved clock position:', e);
      }
    }
    // Default position in top-right corner with some margin
    return { x: window.innerWidth - 280, y: 80 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [calendarDate, setCalendarDate] = useState(() => {
    const now = new Date();
    return { month: now.getMonth(), year: now.getFullYear() };
  });
  const clockRef = useRef(null);
  const { effectiveTheme, THEME_DARK } = useContext(ThemeContext);
  const isDarkMode = effectiveTheme === THEME_DARK;

  const cloudLayers = useMemo(() => (
    Array.from({ length: 4 }, (_, idx) => ({
      id: idx,
      top: 44 + Math.random() * 8, // Position clouds around the middle area
      duration: (90 + idx * 16 + Math.random() * 24) * 2,
      delay: idx * 3,
      scale: 0.9 + Math.random() * 0.4
    }))
  ), []);

  const starField = useMemo(() => (
    Array.from({ length: 20 }, (_, idx) => ({
      id: idx,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: (Math.random() * 2.5 + 1.5) * 3,
      delay: Math.random() * 3,
      duration: (2.5 + Math.random() * 2.5) * 8
    }))
  ), []);

  const trafficClouds = useMemo(() => (
    Array.from({ length: 8 }, (_, idx) => ({
      id: idx,
      top: 4 + idx * 5.5,
      duration: (45 + idx * 9 + (idx % 3) * 14) * 2,
      delay: -(idx * 13 + (idx % 2) * 17),
      scale: 0.7 + (idx % 4) * 0.18,
    }))
  ), []);

  // Update digital time display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // Update every 10 seconds

    return () => clearInterval(timer);
  }, []);

  // ESC exits fullscreen; mouse movement no longer does — only the exit button or ESC
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [isFullscreen]);

  // Auto-fullscreen after 40 minutes of idle — paused while already in fullscreen
  useEffect(() => {
    if (isFullscreen) return;

    const IDLE_MS = 40 * 60 * 1000;
    let idleTimer = setTimeout(() => setIsFullscreen(true), IDLE_MS);

    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setIsFullscreen(true), IDLE_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));

    return () => {
      clearTimeout(idleTimer);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, [isFullscreen]);

  // Handle mouse down event to start dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.close-btn') 
      || e.target.closest('button') 
      || e.target.closest('.clock-calendar')) return; // Don't start drag when interacting with controls
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Handle mouse move event when dragging
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newPosition = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    setPosition(newPosition);
  }, [isDragging, dragStart]);

  // Handle mouse up event to stop dragging
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Save position to localStorage when dragging stops
      localStorage.setItem('clockPosition', JSON.stringify(position));
    }
    setIsDragging(false);
  }, [isDragging, position]);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Save position to localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.setItem('clockPosition', JSON.stringify(position));
    };
  }, [position]);

  const digitalTime = useMemo(() => (
    currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  ), [currentTime]);

  const goToPreviousMonth = () => {
    setCalendarDate((prev) => {
      const newMonth = prev.month - 1;
      if (newMonth < 0) {
        return { month: 11, year: prev.year - 1 };
      }
      return { ...prev, month: newMonth };
    });
  };

  const goToNextMonth = () => {
    setCalendarDate((prev) => {
      const newMonth = prev.month + 1;
      if (newMonth > 11) {
        return { month: 0, year: prev.year + 1 };
      }
      return { ...prev, month: newMonth };
    });
  };

  const resetToToday = () => {
    const now = new Date();
    setCalendarDate({ month: now.getMonth(), year: now.getFullYear() });
  };

  const buildMonthWeeks = (month, year) => {
    const today = currentTime;
    const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const adjustedFirstDay = (firstDay.getDay() + 6) % 7;
    const daysInMonth = lastDay.getDate();
    const totalCells = Math.ceil((adjustedFirstDay + daysInMonth) / 7) * 7;
    const weeks = [];
    let week = [];
    for (let cell = 0; cell < totalCells; cell++) {
      const dayNumber = cell - adjustedFirstDay + 1;
      if (cell < adjustedFirstDay || dayNumber > daysInMonth) {
        week.push(null);
      } else {
        week.push({ day: dayNumber, isToday: isCurrentMonth && dayNumber === today.getDate() });
      }
      if (week.length === 7) { weeks.push(week); week = []; }
    }
    return weeks;
  };

  const calendarData = useMemo(() => {
    const { month, year } = calendarDate;
    const today = currentTime;
    const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const adjustedFirstDay = (firstDay.getDay() + 6) % 7; // convert Sunday=6
    const daysInMonth = lastDay.getDate();
    const totalCells = Math.ceil((adjustedFirstDay + daysInMonth) / 7) * 7;
    const weeks = [];
    let week = [];

    for (let cell = 0; cell < totalCells; cell += 1) {
      const dayNumber = cell - adjustedFirstDay + 1;
      if (cell < adjustedFirstDay || dayNumber > daysInMonth) {
        week.push(null);
      } else {
        const isToday = isCurrentMonth && dayNumber === today.getDate();
        week.push({ day: dayNumber, isToday });
      }

      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    }

    return { weeks, month, year };
  }, [calendarDate, currentTime]);

  return (
    <>
    <div
      ref={clockRef}
      className="detachable-clock"
      style={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        zIndex: 1050,
        padding: '12px',
        backgroundColor: 'var(--card-bg, #fff)',
        borderRadius: '12px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        border: '1px solid var(--border-color, #dee2e6)',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        width: '285px',
        height: '575px', // Extra room to keep the calendar clear of the orbit visuals
        overflow: 'hidden',
        '--weekend-bg': isDarkMode ? 'rgba(0, 123, 255, 0.28)' : 'rgba(0, 123, 255, 0.18)'
      }}
      onMouseDown={handleMouseDown}
    >
      <div className={`clock-atmosphere ${isDarkMode ? 'clock-atmosphere-night' : 'clock-atmosphere-day'}`}>
        {!isDarkMode && cloudLayers.map((cloud) => (
          <div
            key={`cloud-${cloud.id}`}
            className="clock-cloud"
            style={{
              top: `${cloud.top}%`,
              animationDuration: `${cloud.duration}s`,
              animationDelay: `${cloud.delay}s`,
              // Clouds feel oversized in light mode, so scale them down by half
              '--clock-cloud-scale': cloud.scale * 0.5
            }}
          />
        ))}
      </div>

      {isDarkMode && (
        <div className="clock-starfield">
          {starField.map((star) => (
            <div
              key={`star-${star.id}`}
              className="clock-star"
              style={{
                top: `${star.top}%`,
                left: `${star.left}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`
              }}
            />
          ))}
        </div>
      )}

      
      <style>
        {`
          .table > :not(caption) > * > * {
            background-color: transparent;
          }

          .calendar-table .weekend-col {
            background-color: var(--weekend-bg);
          }
          .calendar-table .weekend-col.bg-primary {
            background-color: #0d6efd !important;
            color: #fff !important;
          }
          .calendar-table .weekend-col:not(.bg-primary) {
            color: #0d6efd !important;
            font-weight: 700;
          }
        `}
      </style>


      <div className="clock-content">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="m-0">Clock</h5>
          <div className="position-absolute d-flex gap-1" style={{ top: '10px', right: '10px' }}>
            <Button
              variant="link"
              size="sm"
              className="close-btn p-0 text-muted"
              style={{ fontSize: '1.1rem' }}
              onClick={() => setIsFullscreen(true)}
              aria-label="Fullscreen clock"
              title="Fullscreen resting view"
            >
              <i className="bi bi-arrows-fullscreen"></i>
            </Button>
            <Button
              variant="link"
              size="sm"
              className="close-btn p-0 text-muted"
              style={{ fontSize: '1.1rem' }}
              onClick={onClose}
              aria-label="Close clock"
            >
              <i className="bi bi-x-circle-fill"></i>
            </Button>
          </div>
        </div>

        <div className="text-center d-flex flex-column align-items-center mt-0">
          <div style={{ width: '100%', padding: '2px 0 0' }}>
            <AnalogClock size={200} />
          </div>

          <div className="mt-0" style={{ fontFamily: 'monospace', fontSize: '1.35rem', letterSpacing: '0.04em' }}>
            {digitalTime}
          </div>

          <div className="clock-calendar mt-2 pt-1 w-100">
            <div className="d-flex justify-content-between align-items-center mb-2 calendar-nav">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={goToPreviousMonth}
                className="py-0 px-1"
                aria-label="Previous month"
              >
                <i className="bi bi-chevron-left"></i>
              </Button>
              
              <h5 className="m-0">{MONTH_NAMES[calendarData.month]} {calendarData.year}</h5>
              
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={goToNextMonth}
                className="py-0 px-1"
                aria-label="Next month"
              >
                <i className="bi bi-chevron-right"></i>
              </Button>
            </div>

            <Table bordered size="sm" className="calendar-table mb-0">
              <thead>
                <tr>
                  {DAY_NAMES.map((day, idx) => (
                    <th
                      key={day}
                      className={`text-center py-1 ${idx >= 5 ? 'weekend-col' : ''}`}
                    >
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calendarData.weeks.map((week, weekIndex) => (
                  <tr key={`week-${weekIndex}`}>
                    {week.map((dayObj, dayIndex) => (
                      <td 
                        key={`day-${weekIndex}-${dayIndex}`} 
                        className={`text-center ${dayObj?.isToday ? 'bg-primary text-white' : ''} ${dayIndex >= 5 ? 'weekend-col' : ''}`}
                        style={{ padding: '0.25rem' }}
                      >
                        {dayObj?.day || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="d-flex justify-content-center" style={{ marginTop: '0.6rem' }}>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={resetToToday}
                className="py-0 px-2"
              >
                <i className="bi bi-calendar-check me-1"></i> {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    {isFullscreen && (() => {
      const todayMonth = currentTime.getMonth();
      const todayYear = currentTime.getFullYear();

      const DIM = [
        { opacity: 1,    filter: 'none'           },  // 0 — current / future
        { opacity: 0.3,  filter: 'grayscale(60%)' },  // 1 — one month back
        { opacity: 0.15, filter: 'grayscale(80%)' },  // 2 — two+ months back
      ];

      const dimLevelFor = (m) => {
        const diff = todayMonth - m;
        if (diff <= 0) return 0;
        if (diff === 1) return 1;
        return 2;
      };

      // Force odd slot count so current month is the exact middle card
      const CARD_W = 252; // approximate px per card including gap (1.2× scaled cards)
      const slotsRaw = Math.max(1, Math.floor(window.innerWidth / CARD_W));
      const slots = slotsRaw % 2 === 0 ? slotsRaw - 1 : slotsRaw;
      const half = (slots - 1) / 2; // exact integer — no rounding needed
      // monthSlots may contain values < 0 or > 11 — those render as invisible placeholders
      const monthSlots = Array.from({ length: slots }, (_, i) => todayMonth - half + i);

      const calendarCard = (month) => {
        const level = dimLevelFor(month);
        return (
          <div style={{
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.8)',
            borderRadius: '14px', padding: '0.96rem 1.2rem',
            opacity: DIM[level].opacity,
            filter: DIM[level].filter,
          }}>
            <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '1.2rem', marginBottom: '0.6rem', color: isDarkMode ? '#f0f4f8' : '#1a202c' }}>
              {MONTH_NAMES[month]}
            </div>
            <Table bordered size="sm" className="calendar-table mb-0">
              <thead>
                <tr>
                  {DAY_NAMES.map((day, idx) => (
                    <th key={day} className={`text-center py-1 ${idx >= 5 ? 'weekend-col' : ''}`}
                      style={{ fontSize: '0.9rem', color: isDarkMode ? '#ccd6f6' : undefined }}>{day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {buildMonthWeeks(month, todayYear).map((week, wi) => (
                  <tr key={`cal-${month}-${wi}`}>
                    {week.map((dayObj, di) => (
                      <td key={`cal-${month}-${wi}-${di}`}
                        className={`text-center ${dayObj?.isToday ? 'bg-primary text-white' : ''} ${di >= 5 ? 'weekend-col' : ''}`}
                        style={{ padding: '0.36rem', fontSize: '0.96rem', color: dayObj?.isToday ? undefined : isDarkMode ? '#e2e8f0' : undefined }}
                      >
                        {dayObj?.day || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        );
      };

      const yearDimLevel = (y) => {
        const diff = todayYear - y;
        if (diff <= 0) return 0;
        if (diff === 1) return 1;
        return 2;
      };

      const yearCard = (year) => (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.8)',
          borderRadius: '14px', padding: '0.6rem 0.7rem',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
          opacity: DIM[yearDimLevel(year)].opacity,
          filter: DIM[yearDimLevel(year)].filter,
          display: 'flex', flexDirection: 'column',
          boxSizing: 'border-box', overflow: 'hidden',
        }}>
          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '1rem', marginBottom: '0.3rem', color: isDarkMode ? '#f0f4f8' : '#1a202c', flexShrink: 0 }}>
            {year}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.25rem', flex: 1, alignContent: 'stretch' }}>
            {MONTH_NAMES.map((name, i) => {
              const isCurrent = year === todayYear && i === todayMonth;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '4px',
                  border: isCurrent ? 'none' : (isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.07)'),
                  backgroundColor: isCurrent ? '#0d6efd' : 'transparent',
                  color: isCurrent ? '#fff' : (isDarkMode ? '#e2e8f0' : '#1a202c'),
                  fontWeight: isCurrent ? 700 : 400,
                  fontSize: '0.78rem',
                }}>
                  {name.slice(0, 3)}
                </div>
              );
            })}
          </div>
        </div>
      );

      // More year slots than month slots — year cards are narrower so more fit edge-to-edge
      // justifyContent: center keeps current year at screen center regardless of count
      const YEAR_CARD_W = 155;
      const yearSlotsRaw = Math.max(1, Math.floor(window.innerWidth / YEAR_CARD_W));
      const yearSlots = yearSlotsRaw % 2 === 0 ? yearSlotsRaw - 1 : yearSlotsRaw;
      const yearHalf = (yearSlots - 1) / 2;
      const yearSlotList = Array.from({ length: yearSlots }, (_, i) => todayYear - yearHalf + i);

      return (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.5rem',
          backgroundColor: isDarkMode ? '#0d1117' : '#f0f4f8',
          '--weekend-bg': isDarkMode ? 'rgba(0, 123, 255, 0.28)' : 'rgba(0, 123, 255, 0.18)',
          overflow: 'hidden',
        }}>
          {/* Atmosphere */}
          <div className={`clock-atmosphere ${isDarkMode ? 'clock-atmosphere-night' : 'clock-atmosphere-day'}`} style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
            {!isDarkMode && cloudLayers.map((cloud) => (
              <div key={`fs-cloud-${cloud.id}`} className="clock-cloud"
                style={{ top: `${cloud.top}%`, animationDuration: `${cloud.duration}s`, animationDelay: `${cloud.delay}s`, '--clock-cloud-scale': cloud.scale }} />
            ))}
          </div>
          {isDarkMode && (
            <div className="clock-starfield" style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
              {starField.map((star) => (
                <div key={`fs-star-${star.id}`} className="clock-star"
                  style={{ top: `${star.top}%`, left: `${star.left}%`, width: `${star.size * 1.5}px`, height: `${star.size * 1.5}px`, animationDelay: `${star.delay}s`, animationDuration: `${star.duration}s` }} />
              ))}
            </div>
          )}

          {/* Traffic clouds — bidirectional, viewport-wide travel */}
          {!isDarkMode && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
              {trafficClouds.map(cloud => (
                <div
                  key={`tc-${cloud.id}`}
                  className="fs-traffic-cloud"
                  style={{
                    top: `${cloud.top}%`,
                    animationName: 'fsCloudRight',
                    animationDuration: `${cloud.duration}s`,
                    animationDelay: `${cloud.delay}s`,
                    '--cloud-scale': cloud.scale,
                  }}
                />
              ))}
            </div>
          )}

          {/* Exit button */}
          <button onClick={() => setIsFullscreen(false)} title="Exit fullscreen (ESC)" style={{
            position: 'absolute', top: '1.2rem', right: '1.5rem',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.35)',
            fontSize: '1.6rem', lineHeight: 1, zIndex: 2,
          }}>
            <i className="bi bi-fullscreen-exit"></i>
          </button>

          {/* Clock + time */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <AnalogClock size={520} />
            <div style={{ fontFamily: 'monospace', fontSize: '2.5rem', letterSpacing: '0.06em', color: isDarkMode ? '#f0f4f8' : '#1a202c', lineHeight: 1 }}>
              {digitalTime}
            </div>
          </div>

          {/* Months — odd count, current month always the exact middle, centered under clock */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '1rem', width: '100%' }}>
            {monthSlots.map((m, i) => (
              <div key={i} style={{ visibility: (m < 0 || m > 11) ? 'hidden' : 'visible', flexShrink: 0 }}>
                {calendarCard(m < 0 || m > 11 ? todayMonth : m)}
              </div>
            ))}
          </div>

          {/* Years — edge-to-edge, each card grows equally, current year centered */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '0.75rem', width: '100%' }}>
            {yearSlotList.map((y, i) => (
              <div key={i} style={{ flex: 1, minWidth: 0, aspectRatio: '1', position: 'relative' }}>{yearCard(y)}</div>
            ))}
          </div>
        </div>
      );
    })()}
    </>
  );
};

export default DetachableClock; 
